import {
  motion,
} from "framer-motion";

import {
  Link,
  useLocation,
} from "react-router-dom";

import {
  getLanguageFromPath,
  getRoutePath,
} from "../site/config/routes";

import sessionItems, {
  sessionsPageContent,
} from "../site/data/sessions";

import Footer from "../template/components/layout/Footer";
import HandwrittenEyebrow from "../template/components/common/HandwrittenEyebrow";
import FaqSection from "../template/components/common/FaqSection";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  SESSIONS PAGE
  ============================================================

  Varsinainen palvelusivu.

  Sisältö tulee sessions.js:stä WordPress-tyylisessä muodossa.
  React vastaa vain layoutista, animaatioista ja käytöksestä.
*/

const portfolioCategoryBySessionSlug = {
  weddings: "weddings",
  couples: "couples",
  portraits: "portraits",
  editorial: "editorial",
  products: "products",
  business: "business",
  memorial: "memorial",
  seasonal: "seasonal",
};

const portfolioCtaByCategory = {
  weddings: {
    fi: "Katso lisää hääkuvia",
    en: "View more wedding photographs",
  },

  couples: {
    fi: "Katso lisää parikuvia",
    en: "View more couple photographs",
  },

  portraits: {
    fi: "Katso lisää henkilökuvia",
    en: "View more portraits",
  },

  editorial: {
    fi: "Katso lisää editorial- ja brändikuvia",
    en: "View more editorial and branding photographs",
  },

  products: {
    fi: "Katso lisää tuotekuvia",
    en: "View more product photographs",
  },

  business: {
    fi: "Katso lisää yritys- ja miljöökuvia",
    en: "View more business and location photographs",
  },

  memorial: {
    fi: "Katso lisää muistotilaisuuden kuvia",
    en: "View more memorial photographs",
  },

  seasonal: {
    fi: "Katso lisää sesonkikuvia",
    en: "View more seasonal photographs",
  },
};
export default function SessionsPage() {
  const location =
    useLocation();

  const language =
    getLanguageFromPath(
      location.pathname
    );

  const localized =
    (value) => {
      if (
        value === null ||
        value === undefined
      ) {
        return "";
      }

      if (
        typeof value ===
        "string"
      ) {
        return value;
      }

      return (
        value[language] ??
        value.fi ??
        value.en ??
        ""
      );
    };

  const page =
    sessionsPageContent;

  const sessions =
    sessionItems
      .filter(
        (item) =>
          item.enabled !== false
      )
      .sort(
        (a, b) =>
          Number(a.order || 0) -
          Number(b.order || 0)
      );

  return (
    <>
      {/* ======================================================
          HERO
          ====================================================== */}

      <section
        className="
          relative
          overflow-hidden
          border-b
          border-border
          bg-canvas
        "
      >
        <motion.div
          className="
            pointer-events-none
            absolute
            inset-y-0
            right-0
            z-0
            w-[58%]
            overflow-hidden

            max-sm:right-[-35%]
            max-sm:w-full
          "
          initial={{
            opacity: 0,
            scale: 1.035,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 1.5,
            delay: 0.15,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
          aria-hidden="true"
        >
          <img
            src="/media/heroes/sessions-hero.webp"
            alt=""
            loading="eager"
            decoding="async"
            className="
              h-full
              w-full
              object-cover
              opacity-[0.34]
              grayscale
              saturate-[0.7]
              contrast-[0.9]
            "
            style={{
              objectPosition:
                "50% 50%",
            }}
          />

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-r
              from-canvas
              via-canvas/55
              to-transparent
            "
          />

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-b
              from-canvas/10
              via-transparent
              to-canvas/40
            "
          />
        </motion.div>

        <div
          className="
            relative
            z-10
            mx-auto
            grid
            min-h-[72svh]
            w-full
            max-w-editorial
            items-end
            gap-12
            px-6
            pb-16
            pt-36

            sm:px-10
            sm:pb-20
            sm:pt-44

            lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.34fr)]
            lg:gap-20
            lg:px-16
            lg:pb-24
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 28,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.9,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <HandwrittenEyebrow
              label={localized(
                page.hero.eyebrow
              )}
            />

            <h1
              className="
                m-0
                max-w-5xl
                font-display
                text-[clamp(3.7rem,10vw,9rem)]
                font-normal
                leading-[0.84]
                tracking-[-0.055em]
                text-text
              "
            >
              {localized(
                page.hero.title
              )}
            </h1>
          </motion.div>

          <motion.div
            className="
              max-w-md
              lg:pb-2
            "
            initial={{
              opacity: 0,
              y: 22,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.85,
              delay: 0.16,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <div
              className="
                mb-7
                h-px
                w-10
                bg-text
              "
              aria-hidden="true"
            />

            <p
              className="
                m-0
                text-sm
                leading-7
                text-muted

                sm:text-base
                sm:leading-8
              "
            >
              {localized(
                page.hero.description
              )}
            </p>

            <a
              href="#session-list"
              className="
                group
                mt-9
                inline-flex
                items-center
                gap-4
                border-b
                border-text
                pb-2
                text-[9px]
                font-semibold
                uppercase
                tracking-[0.2em]
                text-text
                no-underline

                sm:text-[10px]
              "
            >
              {localized(
                page.hero.indexLabel
              )}

              <span
                className="
                  transition-transform
                  duration-300
                  group-hover:translate-y-1
                "
                aria-hidden="true"
              >
                ↓
              </span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* ======================================================
          INTRO
          ====================================================== */}

      <section
        className="
          border-b
          border-border
          bg-surface
        "
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-editorial
            gap-12
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:grid-cols-12
            lg:px-16
            lg:py-40
          "
        >
          <motion.div
            className="
              lg:col-span-3
            "
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.4,
            }}
            transition={{
              duration: 0.8,
            }}
          >
            <p
              className="
                m-0
                text-[9px]
                font-medium
                uppercase
                tracking-[0.28em]
                text-muted

                sm:text-[10px]
              "
            >
              {localized(
                page.intro.eyebrow
              )}
            </p>
          </motion.div>

          <motion.div
            className="
              lg:col-span-8
              lg:col-start-5
            "
            initial={{
              opacity: 0,
              y: 28,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.25,
            }}
            transition={{
              duration: 0.9,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <h2
              className="
                m-0
                max-w-4xl
                font-display
                text-[clamp(3rem,6vw,6.5rem)]
                font-normal
                leading-[0.9]
                tracking-[-0.05em]
                text-text
              "
            >
              {localized(
                page.intro.title
              )}
            </h2>

            <p
              className="
                mb-0
                mt-10
                max-w-2xl
                text-base
                leading-8
                text-muted

                sm:text-lg
                sm:leading-9
              "
            >
              {localized(
                page.intro.text
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ======================================================
          SESSION LIST
          ====================================================== */}

      <section
        id="session-list"
        className="
          bg-canvas
        "
      >
        <div
          className="
            mx-auto
            w-full
            max-w-editorial
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:px-16
            lg:py-40
          "
        >
          {sessions.map(
            (
              item,
              index
            ) => {
              const content =
                item.content?.[
                  language
                ] ??
                item.content?.fi ??
                item.content?.en ??
                {};

              const imageFirst =
                index % 2 === 0;

              const portfolioCategory =
                portfolioCategoryBySessionSlug[
                  item.slug
                ] ||
                item.slug;

              const portfolioCta =
                portfolioCtaByCategory[
                  portfolioCategory
                ]?.[language] ??
                (
                  language === "en"
                    ? "View more photographs"
                    : "Katso lisää kuvia"
                );

              return (
                <motion.article
                  key={item.slug}
                  id={item.slug}
                  className="
                    grid
                    gap-10
                    border-t
                    border-border
                    py-16

                    sm:py-20

                    lg:grid-cols-12
                    lg:items-center
                    lg:gap-x-16
                    lg:py-28
                  "
                  initial={{
                    opacity: 0,
                    y: 34,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.12,
                  }}
                  transition={{
                    duration: 0.9,
                    ease: [
                      0.22,
                      1,
                      0.36,
                      1,
                    ],
                  }}
                >
                  <div
                    className={
                      imageFirst
                        ? "lg:col-span-7"
                        : "lg:col-span-7 lg:col-start-6 lg:row-start-1"
                    }
                  >
                    <div
                      className="
                        relative
                        aspect-[4/5]
                        overflow-hidden
                        bg-surface

                        sm:aspect-[4/3]
                        lg:aspect-[5/6]
                      "
                    >
                      <img
                        src={
                          item.image
                        }
                        alt={
                          content.alt ||
                          content.title ||
                          ""
                        }
                        loading="lazy"
                        className="
                          h-full
                          w-full
                          object-cover
                          transition-transform
                          duration-[1100ms]
                          ease-out

                          hover:scale-[1.015]
                        "
                      />

                      <span
                        className="
                          absolute
                          right-5
                          top-5
                          flex
                          h-11
                          w-11
                          items-center
                          justify-center
                          rounded-full
                          border
                          border-white/50
                          bg-black/10
                          text-[9px]
                          tracking-[0.16em]
                          text-white
                          backdrop-blur-sm

                          sm:right-7
                          sm:top-7
                        "
                      >
                        {item.id}
                      </span>
                    </div>
                  </div>

                  <div
                    className={
                      imageFirst
                        ? "min-w-0 lg:col-span-4 lg:col-start-9"
                        : "min-w-0 lg:col-span-4 lg:col-start-1 lg:row-start-1"
                    }
                  >
                    <p
                      className="
                        mb-5
                        text-[9px]
                        font-medium
                        uppercase
                        tracking-[0.28em]
                        text-muted

                        sm:text-[10px]
                      "
                    >
                      {content.eyebrow}
                    </p>

                    <h2
                      className="
                        m-0
                        max-w-full
                        min-w-0
                        break-words
                        font-display
                        text-[clamp(2.7rem,4.2vw,5.2rem)]
                        font-normal
                        leading-[0.9]
                        tracking-[-0.05em]
                        text-text

                        [hyphens:auto]
                        [overflow-wrap:anywhere]
                      "
                    >
                      {content.title}
                    </h2>

                    <p
                      className="
                        mb-0
                        mt-8
                        text-base
                        leading-8
                        text-muted
                      "
                    >
                      {content.description}
                    </p>

                    <p
                      className="
                        mb-0
                        mt-8
                        border-l
                        border-text
                        pl-5
                        font-display
                        text-xl
                        italic
                        leading-7
                        text-text

                        sm:text-2xl
                        sm:leading-8
                      "
                    >
                      “{content.lead}”
                    </p>

                    {Array.isArray(
                      content.details
                    ) &&
                      content.details
                        .length >
                        0 && (
                        <ul
                          className="
                            mt-10
                            border-t
                            border-border
                          "
                        >
                          {content.details.map(
                            (
                              detail,
                              detailIndex
                            ) => (
                              <li
                                key={
                                  detail
                                }
                                className="
                                  flex
                                  gap-4
                                  border-b
                                  border-border
                                  py-4
                                  text-sm
                                  leading-6
                                  text-muted
                                "
                              >
                                <span
                                  className="
                                    shrink-0
                                    font-display
                                    italic
                                    text-text
                                  "
                                  aria-hidden="true"
                                >
                                  {String(
                                    detailIndex +
                                      1
                                  ).padStart(
                                    2,
                                    "0"
                                  )}
                                </span>

                                <span>
                                  {detail}
                                </span>
                              </li>
                            )
                          )}
                        </ul>
                      )}
                    <Link
                      to={`${getRoutePath(
                        "portfolio",
                        language
                      )}?category=${encodeURIComponent(
                        portfolioCategory
                      )}#portfolio-gallery`}
                      className="
                        group
                        mt-10
                        inline-flex
                        items-center
                        gap-4
                        border-b
                        border-text
                        pb-2
                        text-[9px]
                        font-semibold
                        uppercase
                        tracking-[0.2em]
                        text-text
                        no-underline

                        sm:text-[10px]
                      "
                    >
                      {portfolioCta}

                      <span
                        className="
                          transition-transform
                          duration-300
                          group-hover:translate-x-1
                        "
                        aria-hidden="true"
                      >
                        →
                      </span>
                    </Link>
                  </div>
                </motion.article>
              );
            }
          )}
        </div>
      </section>

      {/* ======================================================
          PROCESS
          ====================================================== */}

      <section
        className="
          border-y
          border-border
          bg-surface
        "
      >
        <div
          className="
            mx-auto
            w-full
            max-w-editorial
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:px-16
            lg:py-40
          "
        >
          <div
            className="
              grid
              gap-10

              lg:grid-cols-[minmax(0,1fr)_minmax(300px,0.4fr)]
              lg:items-end
              lg:gap-20
            "
          >
            <div>
              <p
                className="
                  mb-6
                  text-[9px]
                  font-medium
                  uppercase
                  tracking-[0.28em]
                  text-muted

                  sm:text-[10px]
                "
              >
                {localized(
                  page.process.eyebrow
                )}
              </p>

              <h2
                className="
                  m-0
                  max-w-4xl
                  font-display
                  text-[clamp(3rem,6vw,6.5rem)]
                  font-normal
                  leading-[0.9]
                  tracking-[-0.05em]
                  text-text
                "
              >
                {localized(
                  page.process.title
                )}
              </h2>
            </div>
          </div>

          <div
            className="
              mt-16
              grid
              border-t
              border-border

              sm:mt-20

              lg:mt-24
              lg:grid-cols-3
            "
          >
            {page.process.items.map(
              (
                step,
                index
              ) => (
                <motion.article
                  key={step.id}
                  className="
                    border-b
                    border-border
                    py-9

                    lg:border-b-0
                    lg:border-r
                    lg:px-8
                    lg:py-12

                    first:lg:pl-0
                    last:lg:border-r-0
                    last:lg:pr-0
                  "
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.35,
                  }}
                  transition={{
                    duration: 0.7,
                    delay:
                      index * 0.08,
                  }}
                >
                  <span
                    className="
                      text-[9px]
                      uppercase
                      tracking-[0.22em]
                      text-muted
                    "
                  >
                    {step.id}
                  </span>

                  <h3
                    className="
                      mb-0
                      mt-8
                      font-display
                      text-3xl
                      font-normal
                      tracking-[-0.035em]
                      text-text

                      sm:text-4xl
                    "
                  >
                    {localized(
                      step.title
                    )}
                  </h3>

                  <p
                    className="
                      mb-0
                      mt-6
                      max-w-sm
                      text-sm
                      leading-7
                      text-muted
                    "
                  >
                    {localized(
                      step.text
                    )}
                  </p>
                </motion.article>
              )
            )}
          </div>
        </div>
      </section>

      {/* ======================================================
          FAQ
          ====================================================== */}

      <FaqSection
        language={language}
        sectionKey="sessions"
      />
      {/* ======================================================
          CTA
          ====================================================== */}

      <section
        className="
          bg-text
          text-canvas
        "
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-editorial
            gap-12
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.32fr)]
            lg:items-end
            lg:gap-20
            lg:px-16
            lg:py-40
          "
        >
          <div>
            <p
              className="
                mb-7
                text-[9px]
                font-medium
                uppercase
                tracking-[0.28em]
                opacity-60

                sm:text-[10px]
              "
            >
              {localized(
                page.cta.eyebrow
              )}
            </p>

            <h2
              className="
                m-0
                max-w-5xl
                font-display
                text-[clamp(3.3rem,7vw,7.5rem)]
                font-normal
                leading-[0.88]
                tracking-[-0.05em]
              "
            >
              {localized(
                page.cta.title
              )}
            </h2>
          </div>

          <div>
            <p
              className="
                m-0
                text-sm
                leading-7
                opacity-70

                sm:text-base
                sm:leading-8
              "
            >
              {localized(
                page.cta.text
              )}
            </p>

            <Link
              to={getRoutePath(
                "contact",
                language
              )}
              className="
                group
                mt-9
                inline-flex
                items-center
                gap-4
                border-b
                border-current
                pb-2
                text-[9px]
                font-semibold
                uppercase
                tracking-[0.2em]
                text-current
                no-underline

                sm:text-[10px]
              "
            >
              {localized(
                page.cta.button
              )}

              <span
                className="
                  transition-transform
                  duration-300
                  group-hover:translate-x-1
                "
                aria-hidden="true"
              >
                →
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer
        language={language}
      />
    </>
  );
}
